/* eslint-disable max-lines */
import React, { useContext, useEffect, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Row, Col, Nav, NavItem, NavLink, TabContent, TabPane, Badge } from 'reactstrap';
import { Context as AuthContext } from '../../../../contexts/AuthContext';
import CardInfo from '../../../../components/Cards/CardInfo';
import PerformanceManagement from '../../../../services/PerformanceManagement';
import { toast } from 'react-toastify';
import ModalContent from '../../../../components/Modals/Modal';
import DataTableWithoutHeaderAndPagination from '../../../../components/Core/DataTableWithoutHeaderAndPagination';
import FeedbackSummaryModal from './Feedback/FeedbackSummaryModal';
import FeedbackForm from './Feedback/FeedbackForm';
import { getFeedbackPendingColumns } from './Feedback/FeedbackPendingColumns';
import OneOnOneSummaryModal from './OneOnOne/OneOnOneSummaryModal';
import OneOnOneForm from './OneOnOneForm';
import { getOneOnOnePendingColumns } from './OneOnOne/OneOnOnePendingColumns';
import ImprovementSummaryModal from './Improvement/ImprovementSummaryModal';
import ImprovementForm from './ImprovementForm';
import { getImprovementPendingColumns } from './Improvement/ImprovementPendingColumns';
import VerbalWarningSummaryModal from './VerbalWarning/VerbalWarningSummaryModal';
import VerbalWarningForm from './VerbalWarningForm';
import { getVerbalWarningPendingColumns } from './VerbalWarning/VerbalWarningPendingColumns';
import DisciplinaryActionSummaryModal from './DisciplinaryAction/DisciplinaryActionSummaryModal';
import DisciplinaryActionForm from './DisciplinaryActionForm';
import { getDisciplinaryActionPendingColumns } from './DisciplinaryAction/DisciplinaryActionPendingColumns';
import PerformanceFeedback from '../../../../services/PerformanceFeedback';
import OneOnOneService from '../../../../services/PerformanceOneOnOne';
import PerformanceImprovementService from '../../../../services/PerformanceImprovement';
import PerformanceVerbalWarningService from '../../../../services/PerformanceVerbalWarning';
import PerformanceDisciplinaryActionService from '../../../../services/PerformanceDisciplinaryAction';
import FinalizeVerbalWarningModal from '../DirectoryList/FinalizeVerbalWarningModal';
import FinalizeDisciplinaryActionModal from '../DirectoryList/FinalizeDisciplinaryActionModal';
import FinalizePerformanceImprovementModal from '../DirectoryList/FinalizePerformanceImprovementModal';
import AnnualReviewService from '../../../../services/AnnualReview';
import { getAnnualReviewPendingColumns } from './AnnualReview/AnnualReviewPendingColumns';
import confirmAlert from '../../../../helpers/confirmAlert';

interface PendingCounts {
  feedback_count: number;
  feedback_data: any[];
  one_on_one_count: number;
  one_on_one_data: any[];
  performance_improvement_count: number;
  performance_improvement_data?: any[];
  verbal_warning_count: number;
  verbal_warning_data?: any[];
  disciplinary_action_count: number;
  disciplinary_action_data?: any[];
}

interface DraftsPendingFinalizationCardProps {
  onModalClose?: () => void;
}

export interface DraftsPendingFinalizationCardRef {
  reload: () => void;
}

const DraftsPendingFinalizationCard = forwardRef<DraftsPendingFinalizationCardRef, DraftsPendingFinalizationCardProps>(
  ({ onModalClose }, ref) => {
    const { token, currentSchool }: any = useContext(AuthContext);
    const [pendingCounts, setPendingCounts] = useState<PendingCounts>({
      feedback_count: 0,
      feedback_data: [],
      one_on_one_count: 0,
      one_on_one_data: [],
      performance_improvement_count: 0,
      performance_improvement_data: [],
      verbal_warning_count: 0,
      verbal_warning_data: [],
      disciplinary_action_count: 0,
      disciplinary_action_data: [],
    });
    const [verbalWarningsPendingFinalization, setVerbalWarningsPendingFinalization] = useState<any[]>([]);
    const [disciplinaryActionsPendingFinalization, setDisciplinaryActionsPendingFinalization] = useState<any[]>([]);
    const [performanceImprovementsPendingFinalization, setPerformanceImprovementsPendingFinalization] = useState<any[]>(
      [],
    );
    const [annualReviewsPending, setAnnualReviewsPending] = useState<any[]>([]);
    const [toggleModal, setToggleModal] = useState(false);
    const [activeTab, setActiveTab] = useState('2');
    const [isLoading, setIsLoading] = useState(false);

    const [itemFeedbackSummaryModal, setItemFeedbackSummaryModal] = useState<any[]>([]);
    const [showFeedbackSummaryModal, setShowFeedbackSummaryModal] = useState(false);
    const [editFeedbackId, setEditFeedbackId] = useState<string>('');
    const [showEditModal, setShowEditModal] = useState(false);

    const [itemOneOnOneSummaryModal, setItemOneOnOneSummaryModal] = useState<any[]>([]);
    const [showOneOnOneSummaryModal, setShowOneOnOneSummaryModal] = useState(false);
    const [editOneOnOneId, setEditOneOnOneId] = useState<string>('');
    const [showEditOneOnOneModal, setShowEditOneOnOneModal] = useState(false);

    const [itemPerformanceImprovementSummaryModal, setItemPerformanceImprovementSummaryModal] = useState<any[]>([]);
    const [showPerformanceImprovementSummaryModal, setShowPerformanceImprovementSummaryModal] = useState(false);
    const [editPerformanceImprovementId, setEditPerformanceImprovementId] = useState<string>('');
    const [showEditPerformanceImprovementModal, setShowEditPerformanceImprovementModal] = useState(false);

    const [itemPerformanceVerbalWarningSummaryModal, setItemPerformanceVerbalWarningSummaryModal] = useState<any[]>([]);
    const [showPerformanceVerbalWarningSummaryModal, setShowPerformanceVerbalWarningSummaryModal] = useState(false);
    const [editPerformanceVerbalWarningId, setEditPerformanceVerbalWarningId] = useState<string>('');
    const [showEditPerformanceVerbalWarningModal, setShowEditPerformanceVerbalWarningModal] = useState(false);

    const [itemPerformanceDisciplinaryActionSummaryModal, setItemPerformanceDisciplinaryActionSummaryModal] = useState<
      any[]
    >([]);
    const [showPerformanceDisciplinaryActionSummaryModal, setShowPerformanceDisciplinaryActionSummaryModal] =
      useState(false);
    const [editPerformanceDisciplinaryActionId, setEditPerformanceDisciplinaryActionId] = useState<string>('');
    const [showEditPerformanceDisciplinaryActionModal, setShowEditPerformanceDisciplinaryActionModal] = useState(false);

    const [finalizeVwOpen, setFinalizeVwOpen] = useState(false);
    const [finalizeVwRecord, setFinalizeVwRecord] = useState<any>(null);
    const [finalizeDaOpen, setFinalizeDaOpen] = useState(false);
    const [finalizeDaRecord, setFinalizeDaRecord] = useState<any>(null);
    const [finalizePiOpen, setFinalizePiOpen] = useState(false);
    const [finalizePiRecord, setFinalizePiRecord] = useState<any>(null);

    const loadPendingCounts = useCallback(async () => {
      if (!currentSchool?.value || !token) {
        return;
      }

      try {
        setIsLoading(true);
        const service = PerformanceManagement(token);
        const [pendingResponse, vwResult, daResult, piResult, arResult] = await Promise.all([
          service.getPendingBySchool(currentSchool.value),
          PerformanceVerbalWarningService(token).listPending(currentSchool.value),
          PerformanceDisciplinaryActionService(token).listPending(currentSchool.value),
          PerformanceImprovementService(token).listPending(currentSchool.value),
          AnnualReviewService(token)
            .listPendingForMe(currentSchool?.value)
            .catch(() => ({ data: [] })),
        ]);

        if (pendingResponse?.data) {
          setPendingCounts(pendingResponse.data);
        }
        setVerbalWarningsPendingFinalization(Array.isArray(vwResult) ? vwResult : []);
        setDisciplinaryActionsPendingFinalization(Array.isArray(daResult) ? daResult : []);
        setPerformanceImprovementsPendingFinalization(Array.isArray(piResult) ? piResult : []);
        setAnnualReviewsPending(Array.isArray(arResult?.data) ? arResult.data : []);
      } catch (error: any) {
        console.error('Error loading pending counts:', error);
        toast.error(error?.response?.data?.message || 'Error loading pending items');
      } finally {
        setIsLoading(false);
      }
    }, [currentSchool, token]);

    const handleApproveOrDeny = async (e: React.FormEvent, action: string, type: string, id: string) => {
      try {
        setIsLoading(true);

        let service;
        if (type === 'feedback') {
          service = PerformanceFeedback(token);
        } else if (type === 'one-on-one') {
          service = OneOnOneService(token);
        } else if (type === 'performance-improvement') {
          service = PerformanceImprovementService(token);
        } else if (type === 'verbal-warning') {
          service = PerformanceVerbalWarningService(token);
        } else if (type === 'disciplinary-action') {
          service = PerformanceDisciplinaryActionService(token);
        } else {
          return toast.error('Invalid type');
        }

        if (action === 'approve') {
          await service?.approve(id);
          toast.success('Approved successfully!');
        } else {
          await service?.reject(id);
          toast.success('Denied successfully!');
        }

        loadPendingCounts();
      } catch (error: any) {
        console.error('Error approving or denying:', error);
        toast.error(error?.response?.data?.message || 'Error approving or denying item');
      } finally {
        setIsLoading(false);
      }
    };

    const handleEdit = (row: any) => {
      setEditFeedbackId(row.id);
      setShowEditModal(true);
    };

    const handleEditSuccess = () => {
      loadPendingCounts();
      setShowEditModal(false);
      setEditFeedbackId('');
    };

    const handleEditOneOnOne = (row: any) => {
      setEditOneOnOneId(row.id);
      setShowEditOneOnOneModal(true);
    };

    const handleEditOneOnOneSuccess = () => {
      loadPendingCounts();
      setShowEditOneOnOneModal(false);
      setEditOneOnOneId('');
    };

    const handleEditPerformanceImprovement = (row: any) => {
      setEditPerformanceImprovementId(row.id);
      setShowEditPerformanceImprovementModal(true);
    };

    const handleEditPerformanceImprovementSuccess = () => {
      loadPendingCounts();
      setShowEditPerformanceImprovementModal(false);
      setEditPerformanceImprovementId('');
    };

    const handleDelete = (e: React.FormEvent, type: string, id: string) => {
      e.preventDefault();
      confirmAlert({
        title: 'Delete',
        messageType: 'DELETE',
        message: 'Are you sure to delete this item?',
        onClickYes: async () => {
          try {
            setIsLoading(true);

            let service;
            if (type === 'feedback') {
              service = PerformanceFeedback(token);
            } else if (type === 'one-on-one') {
              service = OneOnOneService(token);
            } else if (type === 'performance-improvement') {
              service = PerformanceImprovementService(token);
            } else if (type === 'verbal-warning') {
              service = PerformanceVerbalWarningService(token);
            } else if (type === 'disciplinary-action') {
              service = PerformanceDisciplinaryActionService(token);
            } else {
              return toast.error('Invalid type');
            }

            await service?.delete(id);
            toast.success('Deleted successfully!');
            loadPendingCounts();
          } catch (error: any) {
            console.error('Error deleting item:', error);
            toast.error(error?.response?.data?.message || 'Error deleting item');
          } finally {
            setIsLoading(false);
          }
        },
      });
    };

    const handleCancelAnnualReview = (e: React.FormEvent, row: any) => {
      e.preventDefault();
      const employeeName = `${row?.user?.firstName || ''} ${row?.user?.lastName || ''}`.trim() || 'this employee';
      confirmAlert({
        title: 'Cancel Annual Review',
        messageType: 'DELETE',
        message: `Cancel the ${row?.cycleYear || ''} annual review for ${employeeName}?`,
        onClickYes: async () => {
          try {
            setIsLoading(true);
            await AnnualReviewService(token).cancel(row.id);
            toast.success('Annual review canceled!');
            loadPendingCounts();
          } catch (error: any) {
            toast.error(error?.response?.data?.message || 'Error canceling the annual review');
          } finally {
            setIsLoading(false);
          }
        },
      });
    };

    const handleEditPerformanceVerbalWarning = (row: any) => {
      setEditPerformanceVerbalWarningId(row.id);
      setShowEditPerformanceVerbalWarningModal(true);
    };

    const handleEditPerformanceVerbalWarningSuccess = () => {
      loadPendingCounts();
      setShowEditPerformanceVerbalWarningModal(false);
      setEditPerformanceVerbalWarningId('');
    };

    const handleEditPerformanceDisciplinaryAction = (row: any) => {
      setEditPerformanceDisciplinaryActionId(row.id);
      setShowEditPerformanceDisciplinaryActionModal(true);
    };

    const handleEditPerformanceDisciplinaryActionSuccess = () => {
      loadPendingCounts();
      setShowEditPerformanceDisciplinaryActionModal(false);
      setEditPerformanceDisciplinaryActionId('');
    };

    const performanceImprovementMergedData = [
      ...(pendingCounts.performance_improvement_data || []).map((item: any) => ({
        ...item,
        pendingRowKind: 'draft',
      })),
      ...performanceImprovementsPendingFinalization.map((item: any) => ({
        ...item,
        pendingRowKind: 'pending_finalization',
      })),
    ];

    const verbalWarningData = [
      ...(pendingCounts.verbal_warning_data || []).map((item: any) => ({ ...item, pendingRowKind: 'draft' })),
      ...verbalWarningsPendingFinalization.map((item: any) => ({ ...item, pendingRowKind: 'pending_finalization' })),
    ];

    const disciplinaryActionData = [
      ...(pendingCounts.disciplinary_action_data || []).map((item: any) => ({ ...item, pendingRowKind: 'draft' })),
      ...disciplinaryActionsPendingFinalization.map((item: any) => ({
        ...item,
        pendingRowKind: 'pending_finalization',
      })),
    ];

    const columnsConfig: Record<string, any[]> = {
      improvement: [],
      feedback: getFeedbackPendingColumns(
        setItemFeedbackSummaryModal,
        setShowFeedbackSummaryModal,
        handleApproveOrDeny,
        handleEdit,
        (e, row) => handleDelete(e, 'feedback', row?.id),
      ),
      one_on_one: getOneOnOnePendingColumns(
        setItemOneOnOneSummaryModal,
        setShowOneOnOneSummaryModal,
        handleApproveOrDeny,
        handleEditOneOnOne,
        (e, row) => handleDelete(e, 'one-on-one', row?.id),
      ),
      performance_improvement: getImprovementPendingColumns(
        setItemPerformanceImprovementSummaryModal,
        setShowPerformanceImprovementSummaryModal,
        handleApproveOrDeny,
        handleEditPerformanceImprovement,
        {
          includeTypeColumn: true,
          onFinalize: (row) => {
            setFinalizePiRecord(row);
            setFinalizePiOpen(true);
          },
          onDelete: (e, row) => handleDelete(e, 'performance-improvement', row?.id),
        },
      ),
      verbal_warning: getVerbalWarningPendingColumns(
        setItemPerformanceVerbalWarningSummaryModal,
        setShowPerformanceVerbalWarningSummaryModal,
        handleApproveOrDeny,
        handleEditPerformanceVerbalWarning,
        {
          includeTypeColumn: true,
          onFinalize: (row) => {
            setFinalizeVwRecord(row);
            setFinalizeVwOpen(true);
          },
          onDelete: (e, row) => handleDelete(e, 'verbal-warning', row?.id),
        },
      ),
      annual_review: getAnnualReviewPendingColumns({
        onOpen: () => {
          handleToggleModal();
        },
        onCancel: handleCancelAnnualReview,
      }),
      disciplinary_action: getDisciplinaryActionPendingColumns(
        setItemPerformanceDisciplinaryActionSummaryModal,
        setShowPerformanceDisciplinaryActionSummaryModal,
        handleApproveOrDeny,
        handleEditPerformanceDisciplinaryAction,
        {
          includeTypeColumn: true,
          onFinalize: (row) => {
            setFinalizeDaRecord(row);
            setFinalizeDaOpen(true);
          },
          onDelete: (e, row) => handleDelete(e, 'disciplinary-action', row?.id),
        },
      ),
    };

    useImperativeHandle(ref, () => ({
      reload: loadPendingCounts,
    }));

    useEffect(() => {
      loadPendingCounts();
    }, [loadPendingCounts]);

    const items = [
      {
        label: 'Feedback',
        count: pendingCounts.feedback_data.length,
        data: pendingCounts.feedback_data,
        type: 'feedback',
        tabId: '2',
      },
      {
        label: 'One on One',
        count: pendingCounts.one_on_one_data.length,
        data: pendingCounts.one_on_one_data,
        type: 'one_on_one',
        tabId: '3',
      },
      {
        label: 'Performance Improvement',
        count: performanceImprovementMergedData.length,
        data: performanceImprovementMergedData,
        type: 'performance_improvement',
        tabId: '4',
      },
      {
        label: 'Verbal Warning',
        count: verbalWarningData.length,
        data: verbalWarningData,
        type: 'verbal_warning',
        tabId: '5',
      },
      {
        label: 'Disciplinary Action',
        count: disciplinaryActionData.length,
        data: disciplinaryActionData,
        type: 'disciplinary_action',
        tabId: '6',
      },
      {
        label: 'Annual Review',
        count: annualReviewsPending.length,
        data: annualReviewsPending,
        type: 'annual_review',
        tabId: '7',
      },
    ];

    useEffect(() => {
      if (toggleModal && items.length > 0) {
        const validTabIds = items.map((item) => item.tabId);
        if (!validTabIds.includes(activeTab)) {
          setActiveTab(items[0].tabId);
        }
      }
    }, [
      toggleModal,
      activeTab,
      pendingCounts,
      verbalWarningsPendingFinalization,
      disciplinaryActionsPendingFinalization,
      performanceImprovementsPendingFinalization,
    ]);

    const handleToggleModal = () => {
      if (toggleModal && onModalClose) {
        onModalClose();
      }
      setToggleModal(!toggleModal);
    };

    const customStyles = {
      navLink: {
        minHeight: '42px',
        paddingTop: '8px',
        cursor: 'pointer',
      },
    };

    return (
      <>
        <CardInfo
          class="bg-primary card-info-mg-button"
          title="Drafts / Pending Finalization"
          icon="fa-clock"
          toggle={handleToggleModal}
        >
          <Row className="mt-4">
            {items.map((item, index) => (
              <Col
                key={index}
                className="col text-center pl-1 pr-1"
                style={index + 1 !== items.length ? { borderRight: '#CCC 1px solid' } : {}}
              >
                <h2 style={{ fontSize: '25px', fontWeight: 900, color: '#525f7f' }}>{item.count}</h2>
                <p style={{ fontSize: '10px' }}>{item.label}</p>
              </Col>
            ))}
          </Row>
        </CardInfo>

        <ModalContent
          title="Drafts / Pending Finalization"
          modal={toggleModal}
          toggle={handleToggleModal}
          size="xl"
          fullscreen={true}
        >
          <Row>
            <Col xs="12">
              <style>{`
                .drafts-tabs-scroll::-webkit-scrollbar {
                  height: 6px;
                }
                .drafts-tabs-scroll::-webkit-scrollbar-thumb {
                  background: #cbd5e1;
                  border-radius: 4px;
                }
                .drafts-tabs-scroll::-webkit-scrollbar-track {
                  background: transparent;
                }
              `}</style>
              <div
                className="drafts-tabs-scroll"
                style={{
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  WebkitOverflowScrolling: 'touch',
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#cbd5e1 transparent',
                }}
              >
                <Nav tabs style={{ flexWrap: 'nowrap' }}>
                  {items.map((item) => (
                    <NavItem key={item.tabId} style={{ flexShrink: 0 }}>
                      <NavLink
                        className={`${activeTab === item.tabId ? 'active' : ''}`}
                        onClick={() => setActiveTab(item.tabId)}
                        style={customStyles.navLink}
                      >
                        <span className="d-flex align-items-center">
                          {item.label}
                          {item.count > 0 && (
                            <Badge
                              color="primary"
                              pill
                              className="ml-2"
                              style={{
                                fontSize: '0.75rem',
                                padding: '0.25rem 0.5rem',
                                fontWeight: 600,
                              }}
                            >
                              {item.count}
                            </Badge>
                          )}
                        </span>
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>
              </div>
            </Col>
            <Col xs="12">
              <TabContent activeTab={activeTab}>
                {items.map((item) => (
                  <TabPane key={item.tabId} tabId={item.tabId}>
                    <DataTableWithoutHeaderAndPagination
                      title=""
                      columns={columnsConfig[item.type]}
                      data={item.data || []}
                      loading={isLoading}
                      button={false}
                      buttonUrl=""
                      onChangeRowsPerPage={(() => {}) as any}
                      onChangePage={(() => {}) as any}
                    />
                  </TabPane>
                ))}
              </TabContent>
            </Col>
          </Row>
        </ModalContent>

        <FeedbackSummaryModal
          isOpen={showFeedbackSummaryModal}
          toggle={() => setShowFeedbackSummaryModal(!showFeedbackSummaryModal)}
          feedbackData={itemFeedbackSummaryModal}
        />

        <FeedbackForm
          isOpen={showEditModal}
          toggle={() => setShowEditModal(false)}
          editMode={true}
          feedbackId={editFeedbackId}
          onSuccess={handleEditSuccess}
        />

        <OneOnOneSummaryModal
          isOpen={showOneOnOneSummaryModal}
          toggle={() => setShowOneOnOneSummaryModal(!showOneOnOneSummaryModal)}
          oneOnOneData={itemOneOnOneSummaryModal}
        />

        <OneOnOneForm
          isOpen={showEditOneOnOneModal}
          toggle={() => setShowEditOneOnOneModal(false)}
          editMode={true}
          oneOnOneId={editOneOnOneId}
          onSuccess={handleEditOneOnOneSuccess}
        />

        <ImprovementSummaryModal
          isOpen={showPerformanceImprovementSummaryModal}
          toggle={() => setShowPerformanceImprovementSummaryModal(!showPerformanceImprovementSummaryModal)}
          performanceImprovementData={itemPerformanceImprovementSummaryModal}
        />

        <ImprovementForm
          isOpen={showEditPerformanceImprovementModal}
          toggle={() => setShowEditPerformanceImprovementModal(false)}
          editMode={true}
          performanceImprovementId={editPerformanceImprovementId}
          onSuccess={handleEditPerformanceImprovementSuccess}
        />

        <VerbalWarningSummaryModal
          isOpen={showPerformanceVerbalWarningSummaryModal}
          toggle={() => setShowPerformanceVerbalWarningSummaryModal(!showPerformanceVerbalWarningSummaryModal)}
          verbalWarningData={itemPerformanceVerbalWarningSummaryModal}
        />

        <VerbalWarningForm
          isOpen={showEditPerformanceVerbalWarningModal}
          toggle={() => setShowEditPerformanceVerbalWarningModal(false)}
          editMode={true}
          verbalWarningId={editPerformanceVerbalWarningId}
          onSuccess={handleEditPerformanceVerbalWarningSuccess}
        />

        <DisciplinaryActionSummaryModal
          isOpen={showPerformanceDisciplinaryActionSummaryModal}
          toggle={() =>
            setShowPerformanceDisciplinaryActionSummaryModal(!showPerformanceDisciplinaryActionSummaryModal)
          }
          DisciplinaryActionData={itemPerformanceDisciplinaryActionSummaryModal}
        />

        <DisciplinaryActionForm
          isOpen={showEditPerformanceDisciplinaryActionModal}
          toggle={() => setShowEditPerformanceDisciplinaryActionModal(false)}
          editMode={true}
          disciplinaryActionId={editPerformanceDisciplinaryActionId}
          onSuccess={handleEditPerformanceDisciplinaryActionSuccess}
        />

        {finalizeVwOpen && finalizeVwRecord && (
          <FinalizeVerbalWarningModal
            isOpen={finalizeVwOpen}
            toggle={() => setFinalizeVwOpen(false)}
            recordId={String(finalizeVwRecord.id)}
            record={finalizeVwRecord}
            onSuccess={() => {
              setFinalizeVwOpen(false);
              setFinalizeVwRecord(null);
              loadPendingCounts();
            }}
          />
        )}

        {finalizeDaOpen && finalizeDaRecord && (
          <FinalizeDisciplinaryActionModal
            isOpen={finalizeDaOpen}
            toggle={() => setFinalizeDaOpen(false)}
            recordId={String(finalizeDaRecord.id)}
            record={finalizeDaRecord}
            onSuccess={() => {
              setFinalizeDaOpen(false);
              setFinalizeDaRecord(null);
              loadPendingCounts();
            }}
          />
        )}

        {finalizePiOpen && finalizePiRecord && (
          <FinalizePerformanceImprovementModal
            isOpen={finalizePiOpen}
            toggle={() => setFinalizePiOpen(false)}
            recordId={String(finalizePiRecord.id)}
            record={finalizePiRecord}
            onSuccess={() => {
              setFinalizePiOpen(false);
              setFinalizePiRecord(null);
              loadPendingCounts();
            }}
          />
        )}
      </>
    );
  },
);

DraftsPendingFinalizationCard.displayName = 'DraftsPendingFinalizationCard';

export default DraftsPendingFinalizationCard;
